import React, { useEffect, useMemo, useState } from "react"
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"
import Modal  from "@react/shared/ui/Modal"
import Button from "@react/shared/ui/Button"
import { createDownPayment } from "../api/down_payment"

const MODE_PCT    = "pct"
const MODE_AMOUNT = "amount"

const formatEuros = cents =>
  new Intl.NumberFormat("fr-FR", { style: "currency", currency: "EUR" }).format((cents || 0) / 100)

const toCents = value => {
  if (value == null || value === "") return 0
  const normalized = String(value).replace(/\s/g, "").replace(",", ".")
  const n = parseFloat(normalized)
  if (isNaN(n)) return 0
  return Math.round(n * 100)
}

const DownPaymentModal = ({ isOpen, projectId, downPayment, onClose }) => {
  const [mode,     setMode]     = useState(MODE_PCT)
  const [pct,      setPct]      = useState(String(downPayment?.default_pct || 30))
  const [amount,   setAmount]   = useState("")
  const [saving,   setSaving]   = useState(false)
  const [errors,   setErrors]   = useState([])

  useEffect(() => {
    if (!isOpen) return
    setMode(MODE_PCT)
    setPct(String(downPayment?.default_pct || 30))
    setAmount("")
    setErrors([])
  }, [isOpen, downPayment])

  const basisCents = downPayment?.contract_basis?.cents || 0

  const previewCents = useMemo(() => {
    if (mode === MODE_PCT) {
      const p = parseFloat(String(pct).replace(",", "."))
      if (isNaN(p) || p <= 0) return 0
      return Math.round(basisCents * p / 100)
    }
    return toCents(amount)
  }, [mode, pct, amount, basisCents])

  const handleSave = () => {
    if (saving) return
    setSaving(true)
    setErrors([])

    const body = mode === MODE_PCT
      ? { percentage: parseFloat(String(pct).replace(",", ".")) }
      : { amount_cents: toCents(amount) }

    createDownPayment(projectId, body)
      .then(response => {
        if (response?.errors) {
          setErrors(Array.isArray(response.errors) ? response.errors : [String(response.errors)])
          return
        }
        if (response?.redirect_to) {
          window.location.href = response.redirect_to
          return
        }
        window.location.reload()
      })
      .catch(() => setErrors([I18n.t("projects.down_payments.errors.network")]))
      .finally(() => setSaving(false))
  }

  const footer = (
    <>
      <div className="text-[12.5px] text-slate-600">
        {previewCents > 0 && (
          <>
            <span className="text-slate-500">{I18n.t("projects.down_payments.form.preview")}</span>
            <span className="ml-2 font-semibold text-slate-900 tabular-nums">{formatEuros(previewCents)}</span>
            {basisCents > 0 && (
              <span className="ml-2 text-slate-500 text-[11.5px]">
                / {formatEuros(basisCents)} {I18n.t("projects.down_payments.form.contract_basis")}
              </span>
            )}
          </>
        )}
      </div>
      <div className="flex items-center gap-2">
        <Button variant="ghost"   onClick={onClose}>{I18n.t("projects.down_payments.form.cancel")}</Button>
        <Button variant="primary" onClick={handleSave} disabled={saving || previewCents <= 0}>
          <FontAwesomeIcon icon={saving ? "spinner" : "check"} spin={saving} />
          {I18n.t("projects.down_payments.form.create")}
        </Button>
      </div>
    </>
  )

  return (
    <Modal
      isOpen  = {isOpen}
      onClose = {onClose}
      eyebrow = {I18n.t("projects.down_payments.form.eyebrow")}
      title   = {I18n.t("projects.down_payments.form.title")}
      footer  = {footer}
    >
      <p className="text-[12.5px] text-slate-600 mb-4">
        {I18n.t("projects.down_payments.form.intro")}
      </p>

      <div className="inline-flex rounded-lg bg-slate-100 p-1 mb-4">
        <button
          type      = "button"
          onClick   = {() => setMode(MODE_PCT)}
          className = {`px-3 py-1.5 text-[12.5px] rounded-md ${mode === MODE_PCT ? "bg-white shadow-sm text-slate-900" : "text-slate-600"}`}
        >
          {I18n.t("projects.down_payments.form.mode_pct")}
        </button>
        <button
          type      = "button"
          onClick   = {() => setMode(MODE_AMOUNT)}
          className = {`px-3 py-1.5 text-[12.5px] rounded-md ${mode === MODE_AMOUNT ? "bg-white shadow-sm text-slate-900" : "text-slate-600"}`}
        >
          {I18n.t("projects.down_payments.form.mode_amount")}
        </button>
      </div>

      {mode === MODE_PCT ? (
        <div>
          <label className="block text-xs font-medium text-slate-700 mb-1.5">
            {I18n.t("projects.down_payments.form.field_pct")}
          </label>
          <div className="relative w-48">
            <input
              value     = {pct}
              onChange  = {e => setPct(e.target.value)}
              placeholder = "30"
              className = "w-full h-10 px-3 pr-10 rounded-md border border-slate-200 text-sm text-right tabular-nums"
            />
            <span className="absolute inset-y-0 right-0 pr-3 inline-flex items-center text-slate-500 text-sm pointer-events-none">%</span>
          </div>
        </div>
      ) : (
        <div>
          <label className="block text-xs font-medium text-slate-700 mb-1.5">
            {I18n.t("projects.down_payments.form.field_amount")}
          </label>
          <div className="relative w-64">
            <input
              value     = {amount}
              onChange  = {e => setAmount(e.target.value)}
              placeholder = "0,00"
              className = "w-full h-10 px-3 pr-10 rounded-md border border-slate-200 text-sm text-right tabular-nums"
            />
            <span className="absolute inset-y-0 right-0 pr-3 inline-flex items-center text-slate-500 text-sm pointer-events-none">€</span>
          </div>
        </div>
      )}

      {errors.length > 0 && (
        <div className="mt-4 px-3 py-2 rounded-md bg-rose-50 border border-rose-200 text-rose-700 text-sm">
          {errors.join(", ")}
        </div>
      )}
    </Modal>
  )
}

export default DownPaymentModal
